import { Route, Routes, useParams } from "react-router-dom";

import useCanUpdateStack from "shared/Stack/access/useCanUpdateStack";
import useBreadcrumbs from "components/Breadcrumbs/useBreadcrumbs";
import useTitle from "hooks/useTitle";
import NotFoundPage from "components/error/NotFoundPage";
import useErrorHandle from "hooks/useErrorHandle";
import PageLoading from "components/loading/PageLoading";

import StackSettingsDetails from "./Details";
import StackSettingsSourceCode from "./SourceCode";
import StackSettingsVendor from "./Vendor";
import StackSettingsBehavior from "./Behavior";
import StackSettingsIntegrations from "./Integrations";
import StackSettingsDeletion from "./Deletion";
import { getStacksBackUrl } from "../helpers";
import { StackSettingsContextData } from "./Context";
import useGetStackSettings from "./useGetStackSettings";
import StackSettingsRoutesLayout from "./components/RoutesLayout";
import StackSettingsIntegrationsWebhook from "./Integrations/Webhook";
import StackSettingsIntegrationsWebhookDelivery from "./Integrations/WebhookDelivery";
import StackSettingsRoles from "./Roles";

const StackSettings = () => {
  const { stackId } = useParams<{ stackId: string }>();

  const { stackSettings, loading, error } = useGetStackSettings(stackId);

  useTitle(stackSettings?.name ? `Settings · ${stackSettings.name}` : "Settings");

  const canUpdateStackFn = useCanUpdateStack();

  useBreadcrumbs(
    [
      {
        title: "Stacks",
        link: getStacksBackUrl(),
      },
      {
        title: stackSettings?.name || "",
        link: `/stack/${stackId}`,
      },
      {
        title: "Stack settings",
      },
    ],
    [stackSettings?.name, stackId]
  );

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  if (loading && !stackSettings) {
    return <PageLoading />;
  }

  if (!stackSettings) {
    return <NotFoundPage />;
  }

  const canUpdateStack = canUpdateStackFn(stackSettings);

  return (
    <StackSettingsContextData.Provider
      value={{
        stackSettings,
        canUpdateStack,
      }}
    >
      <Routes>
        <Route element={<StackSettingsRoutesLayout />}>
          <Route index element={<StackSettingsDetails />} />
          <Route path="source-code" element={<StackSettingsSourceCode />} />
          <Route path="vendor" element={<StackSettingsVendor />} />
          <Route path="behavior" element={<StackSettingsBehavior />} />
          <Route path="integrations">
            <Route index path="*" element={<StackSettingsIntegrations />} />
          </Route>
          <Route path="roles" element={<StackSettingsRoles />} />
          <Route path="deletion" element={<StackSettingsDeletion />} />
        </Route>
        <Route
          path="integrations/webhook/:webhookId"
          element={<StackSettingsIntegrationsWebhook />}
        />
        <Route path="integrations/webhook/:webhookId/delivery/:deliveryId">
          <Route index path="*" element={<StackSettingsIntegrationsWebhookDelivery />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </StackSettingsContextData.Provider>
  );
};

export default StackSettings;
